'use client';

/**
 * AddressRenderer Component
 *
 * Renders ADDRESS field type with structured address fields.
 * Supports display, edit, and inline modes.
 */

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { getNeonStyles, type NeonAccent } from '../../../theme/palette';
import { transitionPresets } from '../../../theme/transitions';
import type { AddressValue, FieldDefinition, FieldMode } from '../../../types/entityFramework';

// ============================================================================
// Types
// ============================================================================

export interface AddressRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value */
  value: unknown; // AddressValue
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
  // Address-specific props
  /** Whether to show country field */
  showCountry?: boolean;
  /** Country options for select dropdown */
  countryOptions?: Array<{ code: string; name: string }>;
}

// ============================================================================
// Default Country Options
// ============================================================================

const DEFAULT_COUNTRY_OPTIONS: Array<{ code: string; name: string }> = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
];

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTextFieldProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $hasError: boolean;
  $size: 'small' | 'medium' | 'large';
}

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledTextFieldProps>(({ theme, $isDark, $neonAccent, $hasError, $size }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const sizeStyles = {
    small: { fontSize: '0.875rem', padding: '8px 12px' },
    medium: { fontSize: '1rem', padding: '12px 16px' },
    large: { fontSize: '1.125rem', padding: '16px 20px' },
  };

  const s = sizeStyles[$size];

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      transition: transitionPresets.input,

      '& input, & textarea': {
        fontSize: s.fontSize,
        padding: s.padding,
      },

      '& fieldset': {
        borderColor: $hasError
          ? theme.palette.error.main
          : neonStyles
            ? neonStyles.border
            : $isDark
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(0, 0, 0, 0.15)',
        transition: transitionPresets.input,
      },

      '&:hover fieldset': {
        borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',

        '& fieldset': {
          borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 2,
          boxShadow: neonStyles ? `0 0 12px ${neonStyles.glow}` : undefined,
        },
      },

      '&.Mui-disabled': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)',
      },
    },

    '& .MuiInputLabel-root': {
      color: $isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      fontSize: '0.875rem',

      '&.MuiInputLabel-shrink': {
        backgroundColor: $isDark ? '#1a1a1a' : '#ffffff',
        padding: '0 6px',
        marginLeft: '-4px',
      },

      '&.Mui-focused': {
        color: neonStyles ? neonStyles.base : theme.palette.primary.main,
      },
    },

    '& .MuiFormHelperText-root': {
      marginLeft: 4,
      marginTop: 6,
    },
  };
});

interface StyledSelectProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $hasError: boolean;
  $size: 'small' | 'medium' | 'large';
}

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledSelectProps>(({ theme, $isDark, $neonAccent, $hasError, $size }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const sizeStyles = {
    small: { fontSize: '0.875rem' },
    medium: { fontSize: '1rem' },
    large: { fontSize: '1.125rem' },
  };

  const s = sizeStyles[$size];

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      transition: transitionPresets.input,
      fontSize: s.fontSize,

      '& fieldset': {
        borderColor: $hasError
          ? theme.palette.error.main
          : neonStyles
            ? neonStyles.border
            : $isDark
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(0, 0, 0, 0.15)',
        transition: transitionPresets.input,
      },

      '&:hover fieldset': {
        borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',

        '& fieldset': {
          borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 2,
          boxShadow: neonStyles ? `0 0 12px ${neonStyles.glow}` : undefined,
        },
      },

      '&.Mui-disabled': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)',
      },
    },

    '& .MuiInputLabel-root': {
      color: $isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      fontSize: '0.875rem',

      '&.MuiInputLabel-shrink': {
        backgroundColor: $isDark ? '#1a1a1a' : '#ffffff',
        padding: '0 6px',
        marginLeft: '-4px',
      },

      '&.Mui-focused': {
        color: neonStyles ? neonStyles.base : theme.palette.primary.main,
      },
    },
  };
});

const DisplayValue = styled(Typography, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
  wordBreak: 'break-word',
  whiteSpace: 'pre-line',
}));

const InlineInput = styled(TextField, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: 'transparent',
      transition: transitionPresets.input,

      '& fieldset': {
        borderColor: 'transparent',
      },

      '&:hover fieldset': {
        borderColor: neonStyles
          ? alpha(neonStyles.border, 0.5)
          : $isDark
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        '& fieldset': {
          borderColor: neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 1,
        },
      },
    },

    '& input': {
      padding: '4px 8px',
    },
  };
});

const FormGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gap: 16,
});

const GridCell = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $span: number; $smSpan?: number }>(({ $span, $smSpan }) => ({
  gridColumn: `span ${$smSpan || $span}`,
  '@media (min-width: 600px)': {
    gridColumn: `span ${$span}`,
  },
}));

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Safely parse address value from unknown input
 */
function parseAddressValue(value: unknown): AddressValue {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const obj = value as Record<string, unknown>;
  return {
    street1: typeof obj['street1'] === 'string' ? obj['street1'] : undefined,
    street2: typeof obj['street2'] === 'string' ? obj['street2'] : undefined,
    city: typeof obj['city'] === 'string' ? obj['city'] : undefined,
    state: typeof obj['state'] === 'string' ? obj['state'] : undefined,
    postalCode: typeof obj['postalCode'] === 'string' ? obj['postalCode'] : undefined,
    country: typeof obj['country'] === 'string' ? obj['country'] : undefined,
  };
}

/**
 * Format address for display as multi-line string
 */
function formatAddressMultiLine(address: AddressValue): string {
  const lines: string[] = [];

  if (address.street1) {
    lines.push(address.street1);
  }
  if (address.street2) {
    lines.push(address.street2);
  }

  const cityStateLine: string[] = [];
  if (address.city) {
    cityStateLine.push(address.city);
  }
  if (address.state) {
    cityStateLine.push(address.state);
  }
  if (address.postalCode) {
    cityStateLine.push(address.postalCode);
  }
  if (cityStateLine.length > 0) {
    lines.push(cityStateLine.join(', '));
  }

  if (address.country) {
    lines.push(address.country);
  }

  return lines.join('\n');
}

/**
 * Format address for display as single-line string
 */
function formatAddressSingleLine(address: AddressValue): string {
  const parts: string[] = [];

  if (address.street1) {
    parts.push(address.street1);
  }
  if (address.street2) {
    parts.push(address.street2);
  }
  if (address.city) {
    parts.push(address.city);
  }
  if (address.state) {
    parts.push(address.state);
  }
  if (address.postalCode) {
    parts.push(address.postalCode);
  }
  if (address.country) {
    parts.push(address.country);
  }

  return parts.join(', ');
}

// ============================================================================
// Component
// ============================================================================

export const AddressRenderer: React.FC<AddressRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled = false,
  size = 'medium',
  neonAccent,
  className,
  showCountry = true,
  countryOptions = DEFAULT_COUNTRY_OPTIONS,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();

  const addressValue = parseAddressValue(value);

  // Handle field change
  const handleFieldChange = (fieldName: keyof AddressValue, newValue: string) => {
    const updatedAddress: AddressValue = {
      ...addressValue,
      [fieldName]: newValue || undefined,
    };
    onChange?.(updatedAddress);
  };

  // Format display value
  const getDisplayValue = (): React.ReactNode => {
    const formatted = formatAddressMultiLine(addressValue);
    if (!formatted) {
      return (
        <Typography
          component="span"
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          -
        </Typography>
      );
    }
    return formatted;
  };

  // Display mode
  if (mode === 'display') {
    return (
      <Box className={className}>
        <DisplayValue variant="body1" $isDark={isDark}>
          {getDisplayValue()}
        </DisplayValue>
      </Box>
    );
  }

  // Inline mode - show single line input
  if (mode === 'inline') {
    return (
      <InlineInput
        value={formatAddressSingleLine(addressValue)}
        onChange={(e) => {
          // In inline mode, we just update street1 as a simple fallback
          // Full address editing should use edit mode
          handleFieldChange('street1', e.target.value);
        }}
        placeholder={field.placeholder || t('placeholder.enterAddress')}
        disabled={disabled || field.disabled}
        error={!!error}
        size="small"
        fullWidth
        $isDark={isDark}
        $neonAccent={neonAccent}
        className={className}
      />
    );
  }

  // Edit mode - show full address form
  return (
    <Box className={className}>
      <FormGrid>
        {/* Street Address 1 */}
        <GridCell $span={12} $smSpan={12}>
          <StyledTextField
            value={addressValue.street1 || ''}
            onChange={(e) => handleFieldChange('street1', e.target.value)}
            label={t('label.streetAddress')}
            placeholder={t('placeholder.streetAddress')}
            disabled={disabled || field.disabled}
            error={!!error}
            required={field.required}
            fullWidth
            variant="outlined"
            $isDark={isDark}
            $neonAccent={neonAccent}
            $hasError={!!error}
            $size={size}
          />
        </GridCell>

        {/* Street Address 2 */}
        <GridCell $span={12} $smSpan={12}>
          <StyledTextField
            value={addressValue.street2 || ''}
            onChange={(e) => handleFieldChange('street2', e.target.value)}
            label={t('label.streetAddress2')}
            placeholder={t('placeholder.streetAddress2')}
            disabled={disabled || field.disabled}
            fullWidth
            variant="outlined"
            $isDark={isDark}
            $neonAccent={neonAccent}
            $hasError={false}
            $size={size}
          />
        </GridCell>

        {/* City */}
        <GridCell $span={6} $smSpan={12}>
          <StyledTextField
            value={addressValue.city || ''}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            label={t('label.city')}
            placeholder={t('label.city')}
            disabled={disabled || field.disabled}
            error={!!error}
            required={field.required}
            fullWidth
            variant="outlined"
            $isDark={isDark}
            $neonAccent={neonAccent}
            $hasError={!!error}
            $size={size}
          />
        </GridCell>

        {/* State/Province */}
        <GridCell $span={6} $smSpan={12}>
          <StyledTextField
            value={addressValue.state || ''}
            onChange={(e) => handleFieldChange('state', e.target.value)}
            label={t('label.stateProvince')}
            placeholder={t('label.stateProvince')}
            disabled={disabled || field.disabled}
            error={!!error}
            fullWidth
            variant="outlined"
            $isDark={isDark}
            $neonAccent={neonAccent}
            $hasError={!!error}
            $size={size}
          />
        </GridCell>

        {/* Postal Code */}
        <GridCell $span={showCountry ? 6 : 12} $smSpan={12}>
          <StyledTextField
            value={addressValue.postalCode || ''}
            onChange={(e) => handleFieldChange('postalCode', e.target.value)}
            label={t('label.postalCode')}
            placeholder="12345"
            disabled={disabled || field.disabled}
            error={!!error}
            fullWidth
            variant="outlined"
            $isDark={isDark}
            $neonAccent={neonAccent}
            $hasError={!!error}
            $size={size}
          />
        </GridCell>

        {/* Country */}
        {showCountry && (
          <GridCell $span={6} $smSpan={12}>
            <StyledFormControl
              fullWidth
              variant="outlined"
              error={!!error}
              disabled={disabled || field.disabled}
              $isDark={isDark}
              $neonAccent={neonAccent}
              $hasError={!!error}
              $size={size}
            >
              <InputLabel>{t('label.country')}</InputLabel>
              <Select
                value={addressValue.country || ''}
                onChange={(e) => handleFieldChange('country', e.target.value as string)}
                label={t('label.country')}
                MenuProps={{
                  slotProps: {
                    backdrop: { invisible: true },
                  },
                }}
              >
                <MenuItem value="">
                  <em>{t('placeholder.selectCountry')}</em>
                </MenuItem>
                {countryOptions.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </GridCell>
        )}

        {/* Error/Help Text */}
        {(error || field.helpText) && (
          <GridCell $span={12} $smSpan={12}>
            <FormHelperText error={!!error} sx={{ marginLeft: 0.5 }}>
              {error || field.helpText}
            </FormHelperText>
          </GridCell>
        )}
      </FormGrid>
    </Box>
  );
};

AddressRenderer.displayName = 'AddressRenderer';

export default AddressRenderer;
